// 카탈로그 파라미터 지정(template.catalogParams) 저작 로직 — 순수 함수 층.
//
// 카탈로그 entry가 선언한 `params`(코드 소유, 전 템플릿 공용) 중 `provenance:'user'`인 것을
// 저작자가 템플릿 수준에서 채우는 축이다(결정 2026-08-06-catalog-param-binding-axis 개정 1).
// 지정 형상은 `{from:'literal', value}` | `{from:'variable', key}`이고, 지정 자리는
// `ReportTemplate.catalogParams[catalogKey][paramName]`이다.
//
// 여기엔 화면이 없다 — 어떤 카탈로그를 보여줄지(참조 수집), 변수 후보를 어떻게 나눌지(accepts
// 대조), 지정이 무엇을 뜻하는지(요약 문구)만 담는다. 렌더는 EditorPage의 CatalogParamsEditor.

import type {
  CatalogEntry,
  CatalogParam,
  ParamBinding,
  ReportTemplate,
  TemplateCatalogParamBindings,
  TemplateVariable,
} from '@safetyhub/shared';

/**
 * entry가 선언한 파라미터 — `params`를 가진 kind는 data/computed 둘뿐이다.
 *
 * 전역 편집기와 바인딩 자리 인라인 블록이 **같은 판정**을 쓰게 하려고 함수로 둔다(kind 검사를
 * 각자 적으면 새 kind가 생겼을 때 한쪽만 따라간다).
 */
export function catalogEntryParams(entry: CatalogEntry | undefined): CatalogParam[] {
  return entry && (entry.kind === 'data' || entry.kind === 'computed') ? (entry.params ?? []) : [];
}

/** 카탈로그 파라미터 출처 표시 라벨 — provenance 코드의 사용자용 설명(저작 편집기·미배치 정보 패널 공용). */
export const CATALOG_PARAM_PROVENANCE_LABEL: Record<string, string> = {
  context: '컨텍스트 자동(보고서·현장)',
  user: '사용자 입력',
  const: '상수',
};

/**
 * 템플릿이 **실제로 참조하는** 카탈로그 key 집합.
 *
 * 전체 카탈로그를 나열하면 파라미터 편집기가 노이즈 벽이 되므로, 이 템플릿이 어떤 식으로든
 * 건드리는 카탈로그만 추린다.
 *
 * ⚠️ **구조 전수 JSON 순회**인 것이 의도다(서버 `collectFormatCatalogSources`와 같은 계열).
 * `catalogKey`는 body 바인딩·prefillFrom·optionsFrom·rangeMap.rangesFrom·형식의 scopeFrom·
 * 위젯 트리처럼 거의
 * 모든 자리에 붙는 직교 축이라, 채널별 전용 워커를 나열하면 한 채널을 빠뜨렸을 때 "참조하는데
 * 편집기에 안 뜨는" 은닉이 된다. 키 이름(`catalogKey`)으로 형상을 알아보므로 새 노드/위젯
 * 종류가 생겨도 따라올 필요가 없다.
 *
 * `dataSources`의 주/보조 소스는 필드명이 달라(`primary`/`lookups[].source`) 순회로 잡히지
 * 않으므로 명시적으로 더한다. 이미 지정이 있는 catalogKey도 무조건 포함한다 — 참조가 사라져도
 * 저장된 지정을 화면에서 지우면 그게 은닉이다(편집·해제 수단이 사라진다).
 */
export function collectTemplateCatalogKeys(template: ReportTemplate): Set<string> {
  const keys = new Set<string>();
  const seen = new Set<object>();
  const walk = (value: unknown): void => {
    if (value === null || typeof value !== 'object') return;
    if (seen.has(value as object)) return;
    seen.add(value as object);
    if (Array.isArray(value)) {
      for (const item of value) walk(item);
      return;
    }
    const obj = value as Record<string, unknown>;
    if (typeof obj.catalogKey === 'string' && obj.catalogKey !== '') keys.add(obj.catalogKey);
    for (const child of Object.values(obj)) walk(child);
  };
  walk(template);

  for (const ds of template.dataSources ?? []) {
    if (ds.kind !== 'assembled') continue;
    if (typeof ds.primary === 'string' && ds.primary !== '') keys.add(ds.primary);
    for (const lookup of ds.lookups ?? []) {
      if (typeof lookup.source === 'string' && lookup.source !== '') keys.add(lookup.source);
    }
  }
  for (const key of Object.keys(template.catalogParams ?? {})) keys.add(key);
  return keys;
}

/**
 * 카탈로그 한 건을 참조하는 소비처 한 형태 — 전역 조망 표의 "누가 이 소스를 쓰나" 열.
 *
 * `kind`는 참조 형태의 축(테스트·후속 분기용), `label`은 저작자가 읽는 문장, `count`는 같은
 * 형태의 참조가 여러 자리일 때의 합산(셀 10개가 같은 rangesFrom을 쓰면 한 줄 ×10).
 */
export interface CatalogConsumerRef {
  kind:
    | 'datasource-primary'
    | 'datasource-lookup'
    | 'variable-prefill'
    | 'variable-options'
    | 'prefill'
    | 'options'
    | 'ranges'
    | 'format-scope'
    | 'suggestions'
    | 'submit-action'
    | 'binding'
    | 'other';
  label: string;
  count: number;
}

/** 소비처 라벨의 영역 접두 — 참조가 템플릿의 어느 층에 있는지(최상위 필드명 기준). */
const CONSUMER_AREA_LABEL: Record<string, string> = {
  body: '본문',
  composerWidgetTree: '작성 폼',
  dataDefs: '데이터셋',
  dataSources: '데이터 소스',
  variables: '변수',
  draftMeta: '업로드 초안(미적용)',
};

/**
 * 이 템플릿에서 카탈로그 key를 **참조하는 모든 자리**의 열거 — 전역 조망 표의 짝 맞춤 대조 원료.
 *
 * `collectTemplateCatalogKeys`가 "어떤 카탈로그를 건드리나"(집합)라면, 이 함수는 "누가 어떻게
 * 건드리나"(형태별 목록)다. 같은 이유로 **구조 전수 순회**가 뼈대다 — `catalogKey`는 거의 모든
 * 자리에 붙는 직교 축이라, 채널별 워커를 나열하면 빠뜨린 채널이 "참조하는데 조망에 안 뜨는"
 * 은닉이 된다. 대신 순회 중 만난 자리를 **담고 있는 속성명**(prefillFrom/rangesFrom/scopeFrom/
 * suggestionsFrom/submitAction)과 Binding 형상(bindingKey 보유)으로 분류하고, 모르는 형상도
 * `other`로 반드시 낸다(분류 불가 ≠ 미표시).
 *
 * 명시 처리 둘:
 *  - `dataSources`의 주/보조 소스는 필드명이 달라(`primary`/`lookups[].source`) 순회로 안 잡힌다.
 *  - `variables`의 prefillFrom/optionsFrom은 순회로도 잡히지만, "어느 변수인지"가 짝 맞춤 대조의
 *    핵심이라 변수 정체를 라벨에 싣기 위해 먼저 처리한다(순회에서는 중복 제외).
 *
 * `template.catalogParams` 서브트리는 건너뛴다 — 지정 자체는 소비처가 아니다. 지정만 있고 이
 * Map에 항목이 없는 카탈로그가 곧 "참조 없음"(유령 지정)이다.
 */
export function collectCatalogConsumers(
  template: ReportTemplate,
): Map<string, CatalogConsumerRef[]> {
  // catalogKey → (kind|label → ref) — 같은 형태는 count 합산.
  const acc = new Map<string, Map<string, CatalogConsumerRef>>();
  const add = (catalogKey: string, kind: CatalogConsumerRef['kind'], label: string): void => {
    if (catalogKey === '') return;
    const byLabel = acc.get(catalogKey) ?? new Map<string, CatalogConsumerRef>();
    acc.set(catalogKey, byLabel);
    const id = `${kind}|${label}`;
    const existing = byLabel.get(id);
    if (existing) existing.count += 1;
    else byLabel.set(id, { kind, label, count: 1 });
  };

  for (const ds of template.dataSources ?? []) {
    if (ds.kind !== 'assembled') continue;
    const name = ds.label || ds.key;
    if (typeof ds.primary === 'string' && ds.primary !== '') {
      add(ds.primary, 'datasource-primary', `데이터 소스 '${name}' 주 소스`);
    }
    for (const lookup of ds.lookups ?? []) {
      if (typeof lookup.source === 'string' && lookup.source !== '') {
        add(lookup.source, 'datasource-lookup', `데이터 소스 '${name}' 보조(lookup)`);
      }
    }
  }

  const handled = new Set<object>();
  for (const v of template.variables ?? []) {
    const name = v.label || v.key;
    if (v.prefillFrom && typeof v.prefillFrom.catalogKey === 'string') {
      add(v.prefillFrom.catalogKey, 'variable-prefill', `변수 ${name}(${v.key}) 초깃값 자동 채움`);
      handled.add(v.prefillFrom);
    }
    if (v.optionsFrom && typeof v.optionsFrom.catalogKey === 'string') {
      add(v.optionsFrom.catalogKey, 'variable-options', `변수 ${name}(${v.key}) 옵션 소스`);
      handled.add(v.optionsFrom);
    }
  }

  const seen = new Set<object>();
  const walk = (value: unknown, path: string[]): void => {
    if (value === null || typeof value !== 'object') return;
    if (seen.has(value as object)) return;
    seen.add(value as object);
    if (Array.isArray(value)) {
      value.forEach((item, i) => walk(item, [...path, String(i)]));
      return;
    }
    const obj = value as Record<string, unknown>;
    if (typeof obj.catalogKey === 'string' && obj.catalogKey !== '' && !handled.has(obj)) {
      const area = CONSUMER_AREA_LABEL[path[0]] ?? path[0];
      // 인덱스 세그먼트를 지운, 이 객체를 담고 있는 가장 가까운 속성명으로 형태를 가른다.
      const holder = [...path].reverse().find((seg) => !/^\d+$/.test(seg));
      if (holder === 'prefillFrom') add(obj.catalogKey, 'prefill', `${area} 자동 채움(prefillFrom)`);
      else if (holder === 'optionsFrom') add(obj.catalogKey, 'options', `${area} 옵션 소스(optionsFrom)`);
      else if (holder === 'rangesFrom')
        add(obj.catalogKey, 'ranges', `${area} 표시 형식 구간 기준표(rangesFrom)`);
      else if (holder === 'scopeFrom')
        add(obj.catalogKey, 'format-scope', `${area} 표시 형식 보조 자료(scopeFrom)`);
      else if (holder === 'suggestionsFrom')
        add(obj.catalogKey, 'suggestions', `${area} 검색 제안(suggestionsFrom)`);
      else if (holder === 'submitAction')
        add(obj.catalogKey, 'submit-action', `${area} 모달 등록 액션(submitAction)`);
      else if (typeof obj.bindingKey === 'string')
        // bindingKey는 라벨에 싣지 않는다 — 셀 수십 개가 각기 다른 키를 가져 한 줄씩 나열되면
        // 조망이 다시 소음이 된다(2026-08-09 SMS 실측: 바인딩만 30여 줄). 같은 라벨로 합산해
        // "본문 바인딩 ×N" 한 줄이 되게 한다.
        add(obj.catalogKey, 'binding', `${area} 바인딩`);
      else add(obj.catalogKey, 'other', `${area} 기타 참조(${holder ?? '최상위'})`);
    }
    for (const [k, child] of Object.entries(obj)) walk(child, [...path, k]);
  };
  for (const [k, v] of Object.entries(template)) {
    // 지정 자체(catalogParams)는 소비처가 아니다 — 여기 키만 있고 참조가 없으면 그게 유령 지정.
    if (k === 'catalogParams') continue;
    walk(v, [k]);
  }

  const out = new Map<string, CatalogConsumerRef[]>();
  for (const [catalogKey, byLabel] of acc) out.set(catalogKey, [...byLabel.values()]);
  return out;
}

/** 저작 편집기에 그릴 파라미터 한 개. */
export interface CatalogParamRowItem {
  param: CatalogParam;
  /** 저작 지정 대상인가 — `provenance:'user'`만 true(context/const는 읽기 전용 표시). */
  authorable: boolean;
  /** 현재 지정(없으면 undefined = 카탈로그 기본 해소에 맡김). authorable=false면 항상 undefined. */
  binding?: ParamBinding;
  /**
   * `validation.required`(폴백 없음)인데 지정이 없다 — 차단이 아니라 경고.
   *
   * ⚠️ 이 배지가 실제로 뜨려면 **카탈로그가 선언을 해야** 한다. 배선은 처음부터 멀쩡했고,
   * 선언한 entry가 0개라 한 번도 뜨지 않았을 뿐이다(결정 2026-08-10). 폴백이 없는 조회를
   * 새로 만들 때 `validation: { required: true }`를 빠뜨리면 그대로 다시 잠든다.
   */
  missingRequired: boolean;
  /** 지정은 있는데 값이 비었다 — 지정이 축을 소유하므로 계층 폴백으로 간다는 안내가 필요한 상태. */
  emptyBinding: boolean;
  /** `{from:'variable'}`인데 그 key의 전역 변수가 템플릿에 없다(삭제·리네임 후 유령 지정). */
  danglingVariable: boolean;
  /** 카탈로그 선언에 없는 파라미터인데 지정만 남았다(entry 제거·파라미터 리네임) — 해제만 가능. */
  unknownParam: boolean;
}

/** 저작 편집기에 그릴 카탈로그 한 개. */
export interface CatalogParamRow {
  catalogKey: string;
  /** 카탈로그 entry 표시명(`group · label`). entry를 못 찾으면 undefined. */
  label?: string;
  /** 카탈로그 entry가 목록에 없다(제거·미로드) — 지정만 남아 있는 상태. */
  unknownEntry: boolean;
  params: CatalogParamRowItem[];
}

/** 값으로 인정하는 형상 — 서버 해소(`resolveFindingRiskMethod`)와 같은 "비어 있지 않은" 판정. */
function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * 저작 편집기가 그릴 목록 — 템플릿이 참조하는 카탈로그 중 **저작 가능한 파라미터가 하나라도 있는**
 * 것만 추린다.
 *
 * `provenance:'context'`(예: 거의 모든 entry의 `id`)만 선언한 카탈로그까지 카드로 그리면 편집기가
 * 읽기 전용 행의 벽이 되고, 정작 고칠 것이 묻힌다. 다만 저작 대상이 하나라도 있는 카탈로그
 * **안에서는** context/const 파라미터도 전부 보여준다 — 무엇이 자동으로 채워지는지 모르면 저작자가
 * 지정의 의미를 판단할 수 없다(은닉 금지).
 *
 * 지정만 남고 entry가 사라진 카탈로그도 목록에 남긴다(unknownEntry) — 편집·해제 수단 보존.
 */
export function buildCatalogParamRows(
  template: ReportTemplate,
  catalog: CatalogEntry[] | undefined,
): CatalogParamRow[] {
  const referenced = collectTemplateCatalogKeys(template);
  const bindings = template.catalogParams ?? {};
  const variables = template.variables ?? [];
  const rows: CatalogParamRow[] = [];

  for (const catalogKey of referenced) {
    const entry = (catalog ?? []).find((e) => e.key === catalogKey);
    const params = catalogEntryParams(entry);
    const saved = bindings[catalogKey] ?? {};
    const hasUserParam = params.some((p) => p.provenance === 'user');
    const items = buildCatalogParamItems(params, saved, variables);
    // 저작 대상도, 해제해야 할 유령 지정도 없으면 카드 자체를 그리지 않는다.
    if (!hasUserParam && !items.some((item) => item.unknownParam)) continue;

    rows.push({
      catalogKey,
      ...(entry ? { label: `${entry.group} · ${entry.label}` } : {}),
      unknownEntry: !entry,
      params: items,
    });
  }
  // 카탈로그 키 알파벳 순 — Set 순회 순서(템플릿 구조 순서)는 저장 형상이 바뀔 때마다 흔들린다.
  return rows.sort((a, b) => a.catalogKey.localeCompare(b.catalogKey));
}

/**
 * 카탈로그 **한 건**의 파라미터 행 목록 — 전역 편집기와 바인딩 자리 인라인 블록의 공용 산출 축.
 *
 * 선언 순서대로 그린 뒤, 선언에 없는데 지정만 남은 이름(entry 제거·파라미터 리네임)을 뒤에 붙인다.
 * 그 유령 지정을 그리지 않으면 화면에서 **영원히 해제할 수 없다**.
 */
export function buildCatalogParamItems(
  params: CatalogParam[],
  saved: Record<string, ParamBinding>,
  variables: TemplateVariable[],
): CatalogParamRowItem[] {
  const orphanNames = Object.keys(saved).filter((name) => !params.some((p) => p.name === name));
  return [
    ...params.map((param) => toRowItem(param, saved[param.name], variables)),
    ...orphanNames.map((name) =>
      toRowItem({ name, provenance: 'user' }, saved[name], variables, true),
    ),
  ];
}

function toRowItem(
  param: CatalogParam,
  binding: ParamBinding | undefined,
  variables: TemplateVariable[],
  unknownParam = false,
): CatalogParamRowItem {
  const authorable = param.provenance === 'user';
  const effective = authorable ? binding : undefined;
  // 좁힌 결과를 먼저 값으로 뽑아 둔다 — 콜백 안에서 union 좁힘에 기대지 않기 위해.
  const literalValue = effective?.from === 'literal' ? effective.value : undefined;
  const variableKey = effective?.from === 'variable' ? effective.key : undefined;
  return {
    param,
    authorable,
    ...(effective ? { binding: effective } : {}),
    missingRequired: authorable && !effective && Boolean(param.validation?.required),
    emptyBinding: effective?.from === 'literal' && isEmptyValue(literalValue),
    danglingVariable:
      variableKey !== undefined && !variables.some((v) => v.key === variableKey),
    unknownParam,
  };
}

/** 파라미터에 꽂을 수 있는 전역 변수 후보 — 권장(accepts 대조 일치)과 그 외로 나눈다. */
export interface ParamVariableCandidates {
  /** `accepts`가 가리키는 형상과 맞는 변수(`optionsFrom.catalogKey`[+`valueField`] 일치). */
  recommended: TemplateVariable[];
  /** 그 외 전부 — **선택 가능하다**(후보에서 빼지 않는다). */
  others: TemplateVariable[];
  /** `accepts` 선언이 있어 권장/그 외 구분이 의미 있는가. false면 한 목록으로 그린다. */
  hasHint: boolean;
}

/**
 * 변수 후보 분류 — `CatalogParam.accepts`(받는 값의 출처 형상)를 변수의 `optionsFrom`과 대조한다.
 *
 * ⚠️ **힌트이지 게이트가 아니다**(결정 §2). 카탈로그 선언은 불완전할 수 있고(값 형상이 실제로
 * 둘 섞여 있다 — 세트 id / 방식 문자열), 대조에 걸리지 않는다는 이유로 선택지를 없애면 그게
 * 은닉이다. 안 맞는 변수도 `others`로 **반드시 남는다** — 정렬·묶음에만 쓴다.
 *
 * `accepts.valueField`가 있으면 그 필드까지 일치해야 권장이다(세트 id를 받는 파라미터에 라벨
 * 필드를 값으로 쓰는 변수가 권장으로 올라오지 않게).
 */
export function paramVariableCandidates(
  param: CatalogParam,
  variables: TemplateVariable[] | undefined,
): ParamVariableCandidates {
  const all = variables ?? [];
  const accepts = param.accepts;
  if (!accepts) return { recommended: [], others: all, hasHint: false };
  const recommended: TemplateVariable[] = [];
  const others: TemplateVariable[] = [];
  for (const v of all) {
    const from = v.optionsFrom;
    const match =
      from?.catalogKey === accepts.catalogKey &&
      (accepts.valueField === undefined || from?.valueField === accepts.valueField);
    (match ? recommended : others).push(v);
  }
  return { recommended, others, hasHint: true };
}

/** 지정 방식 — 편집기 라디오/셀렉트의 축(미지정 포함). */
export type ParamBindingMode = 'none' | 'literal' | 'variable';

/** 현재 지정의 방식. */
export function paramBindingMode(binding: ParamBinding | undefined): ParamBindingMode {
  if (!binding) return 'none';
  return binding.from;
}

/**
 * 지정 한 줄 요약 — "무엇이 이 값을 정하는가"를 문장으로. 미배치 위젯 정보 패널과 편집기가 공유한다.
 *
 * 상속(카탈로그 기본 해소)·자동(컨텍스트/상수)·저작한 값이 서로 구분돼 읽혀야 한다
 * (feedback-authoring-ui-resolved-value-hides-inherit-vs-auto).
 */
export function catalogParamBindingSummary(
  param: CatalogParam,
  binding: ParamBinding | undefined,
  variables: TemplateVariable[] | undefined,
): string {
  if (param.provenance === 'context') return CATALOG_PARAM_PROVENANCE_LABEL.context;
  if (param.provenance === 'const') {
    return isEmptyValue(param.constValue)
      ? CATALOG_PARAM_PROVENANCE_LABEL.const
      : `${CATALOG_PARAM_PROVENANCE_LABEL.const} ${formatParamValue(param.constValue)}`;
  }
  // 미지정의 결과는 조회마다 다르다 — 폴백이 없다고 선언한 조회는 "값 없음"으로 끝난다.
  if (!binding) {
    return param.validation?.required ? '미지정 — 값 없음' : '미지정 — 기본 조회 방식';
  }
  if (binding.from === 'literal') {
    return isEmptyValue(binding.value)
      ? '직접 입력 (값 없음)'
      : `직접 입력 ${formatParamValue(binding.value)}`;
  }
  const v = (variables ?? []).find((x) => x.key === binding.key);
  return v
    ? `전역 변수 ${v.label || binding.key}(${binding.key})`
    : `전역 변수 ${binding.key} (없는 변수)`;
}

/** 안내 한 줄의 강조도 — 저작자가 손봐야 할 상태(warn)와 그냥 사실(muted)을 가른다. */
export interface CatalogParamNote {
  text: string;
  tone: 'warn' | 'muted';
}

/**
 * "지금 이 지정이면 무슨 일이 일어나는가" 한 줄 — **카탈로그 선언을 그대로 읽는다**.
 *
 * 범용 저작 UI는 특정 조회의 도메인 규칙을 알아선 안 된다. 종전에는 이 자리에 위험성평가 전용
 * 규칙('점검 → 현장 → 시스템 기본 순')이 하드코딩돼 있었고, 조회가 늘면 그대로 거짓말이 된다
 * (결정 2026-08-10 §2). 이제 문장은 `param.whenAbsent` 선언에서 오고, 선언이 없으면 조회 이름을
 * 지어내지 않는 일반 문구로 degrade한다.
 *
 * 지정이 있어도 값이 비면 결과는 미지정과 같으므로, 그 경우에도 같은 선언을 이어 붙여 읽힌다
 * (한 문장에 뭉치면 서로 어긋나 읽힌다 — 2026-08-10 사용자 지적).
 */
export function catalogParamAbsentNote(
  item: CatalogParamRowItem,
): CatalogParamNote | undefined {
  if (!item.authorable) return undefined;
  const { param, binding } = item;
  // 필수 축은 `validation.required`가 정본이다 — 새 필드를 만들지 않는다(팀 확인, 2026-08-10).
  const required = Boolean(param.validation?.required);
  const declared = param.whenAbsent?.trim();
  // 폴백 없음/있음 각각의 "값이 없을 때 결과" — 두 상태(미지정·빈 값)가 공유하는 꼬리 문장.
  const absentResult = required
    ? '이 조회는 값을 내지 못합니다.'
    : declared
      ? declared
      : '이 조회가 선언한 기본 동작에 맡겨집니다.';

  if (!binding) {
    return required
      ? { tone: 'warn', text: `필수 — 지정하지 않으면 ${absentResult}` }
      : { tone: 'muted', text: `미지정이면 ${absentResult}` };
  }
  // 없는 변수를 가리키는 지정도 값이 비는 건 마찬가지다 — 두 상태에 다른 결말을 적으면
  // 바로 아래 유령 변수 경고와 서로 어긋나 읽힌다.
  if (item.emptyBinding || item.danglingVariable) {
    return {
      tone: 'warn',
      text: `값이 비어 있어 아직 효력이 없습니다 — 채우기 전까지는 ${absentResult}`,
    };
  }
  return { tone: 'muted', text: '지정한 이 값이 쓰입니다 — 미지정 시 동작보다 우선합니다.' };
}

/**
 * "그래서 지금 실제로 무슨 값이 쓰이나" 한 줄 — 미리보기 응답의 해소 스탬프(`effective`)를 읽는다.
 *
 * 선언만으로는 저작자가 자기 선택을 검증할 수 없다는 게 이 프로젝트에서 이미 확인된 패턴이라
 * (feedback-authoring-panel-must-show-resolved-values-not-just-source), 실제 해소 값을 같이 보인다.
 * 컨텍스트가 없으면 **값을 지어내지 않고** 무엇을 하면 보이는지만 말한다(결정 2026-08-10 §3).
 */
export function catalogParamEffectiveNote(
  effective: { id?: string; method?: string } | undefined,
  hasPreviewContext: boolean,
  /**
   * `effective.id`가 가리키는 대상의 사람이 읽는 이름(`catalogEffectiveIdName` 산출).
   * 모르면 생략 — **지어내지 않고** raw id로 degrade한다(가짜 이름이 id보다 나쁘다).
   */
  effectiveName?: string,
): CatalogParamNote {
  if (!hasPreviewContext) {
    // 안내는 "무엇을 하면 보이나"까지 말해야 한다 — 어디로 가야 하는지 없이 조건만 적으면
    // 저작자가 그 자리를 찾아 헤맨다(2026-08-10 P5). 화면에는 이 문구 옆에 실제 진입 버튼이 붙는다.
    return {
      tone: 'muted',
      text: '미리보기 컨텍스트를 고르면 실제 적용 값을 보여줍니다 — 상단 미리보기 막대의 [실제 데이터 반영]에서 조직·현장·점검을 선택하세요.',
    };
  }
  const parts = [
    ...(effective?.method ? [effective.method] : []),
    ...(effective?.id
      ? [`세트 ${effectiveName ? `'${effectiveName}'` : effective.id}`]
      : []),
  ];
  return parts.length === 0
    ? { tone: 'muted', text: '이 컨텍스트에서는 낙점된 값이 없습니다.' }
    : { tone: 'muted', text: `현재 컨텍스트 적용 값: ${parts.join(' · ')}` };
}

/**
 * 해소 스탬프의 `effective.id`에 붙일 **이름** — 이미 받아 둔 목록 행에서 찾는다(서버 왕복 없음).
 *
 * 스탬프는 `{id, method}`뿐이라 화면에 raw id(`cmkoob7t1…`)가 그대로 뜬다. 이름은 그 id를 **값으로
 * 공급하는 목록**이 이미 갖고 있으므로(위험성평가 세트 목록의 `{id, name}`), 카탈로그가 선언한
 * `accepts`(이 파라미터가 받는 값의 출처 목록)를 따라가 조인한다 — 어느 조회가 어느 목록에서 오는지는
 * **선언이 알고 있고**, 여기서 도메인 이름을 하드코딩하면 조회가 늘 때마다 거짓말이 된다.
 *
 * `rowsOf`는 호출부가 이미 들고 있는 행(prefill/미리보기 응답)을 돌려주면 된다. 행이 없거나 이름
 * 필드가 없으면 undefined — 호출부가 id 표시로 degrade한다.
 */
export function catalogEffectiveIdName(
  entry: CatalogEntry | undefined,
  id: string,
  rowsOf: (catalogKey: string) => unknown,
): string | undefined {
  for (const param of catalogEntryParams(entry)) {
    const accepts = param.accepts;
    if (!accepts) continue;
    const rows = rowsOf(accepts.catalogKey);
    if (!Array.isArray(rows)) continue;
    // valueField 미선언이면 식별자 관례 'id' — accepts는 "필드 무관 대조"를 허용하지만, 여기서
    // 찾는 것은 **식별자**라 대조할 필드가 필요하다.
    const valueField = accepts.valueField ?? 'id';
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      const record = row as Record<string, unknown>;
      if (record[valueField] !== id) continue;
      const name = record.name ?? record.label;
      if (typeof name === 'string' && name !== '') return name;
    }
  }
  return undefined;
}

/** 값 한 줄 표기 — 문자열은 따옴표, 그 외는 JSON 축약(객체·배열도 무크래시). */
export function formatParamValue(value: unknown, max = 40): string {
  if (value === undefined) return '(없음)';
  if (value === null) return 'null';
  if (typeof value === 'string') {
    return value.length > max ? `"${value.slice(0, max - 1)}…"` : `"${value}"`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    // JSON.stringify는 타입상 string이지만 함수/심볼에는 undefined를 돌려준다(런타임 사실).
    const s = JSON.stringify(value) as string | undefined;
    if (s === undefined) return String(value);
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  } catch {
    return String(value);
  }
}

/**
 * 파라미터 지정 갱신 — 값 형상을 정규화해 다음 `catalogParams`를 만든다.
 * `binding`이 undefined면 **지정 해제**(파라미터 키 제거, 카탈로그가 비면 카탈로그 키도 제거).
 * 전체가 비면 undefined를 돌려준다 — 빈 객체를 남기면 저장 형상이 지저분해진다(`prefillFrom` 동형).
 */
export function setCatalogParamBinding(
  current: TemplateCatalogParamBindings | undefined,
  catalogKey: string,
  paramName: string,
  binding: ParamBinding | undefined,
): TemplateCatalogParamBindings | undefined {
  const next: TemplateCatalogParamBindings = { ...(current ?? {}) };
  const params = { ...(next[catalogKey] ?? {}) };
  if (binding) params[paramName] = binding;
  else delete params[paramName];
  if (Object.keys(params).length === 0) delete next[catalogKey];
  else next[catalogKey] = params;
  return Object.keys(next).length === 0 ? undefined : next;
}

/** 필수인데 미지정인 파라미터 목록 — 편집기 상단·섹션 배지의 경고 소스(차단 아님). */
export function missingRequiredCatalogParams(
  rows: CatalogParamRow[],
): { catalogKey: string; paramName: string; label: string }[] {
  return rows.flatMap((row) =>
    row.params
      .filter((item) => item.missingRequired)
      .map((item) => ({
        catalogKey: row.catalogKey,
        paramName: item.param.name,
        label: item.param.label || item.param.name,
      })),
  );
}
